import React, { type HTMLAttributes } from 'react'
import styled, { useTheme } from 'styled-components'
import { ChevronLeft, X } from 'react-feather'
import useScreenSize from 'hooks/useScreenSize'
import { InjectedProps } from './types'
import { Text, TextVariant } from '../../components/Text'
import { Flex } from '../../components/Flex'

interface Props extends InjectedProps {
  title?: string
  hideCloseButton?: boolean
  bodyPadding?: string
  children: React.ReactNode
  style?: React.CSSProperties
  centerTitle?: boolean
  titleFontVariant?: TextVariant
  onReturnArrowClick?: () => void
  subTitle?: string
}

const StyledModal = styled.div<HTMLAttributes<HTMLDivElement>>`
  background: ${({ theme }) => theme.colors.backgroundContrast};
  box-shadow: 0 13px 20px rgba(0, 0, 0, 0.25);

  width: 100%;
  z-index: ${({ theme }) => theme.zIndices.modalOverlay};
  overflow-y: auto;
  padding: 24px;
  @-moz-document url-prefix() {
    background: ${({ theme }) => theme.colors.backgroundContrast} !important;
  }
  /*
   * 90vh, not 90%. A percentage max-height resolves against the parent's
   * height and silently becomes "none" whenever that height is auto — so a
   * modal whose wrapper ever loses its fixed inset would grow past the
   * screen and its own overflow-y would never engage. vh does not depend on
   * anything above it.
   */
  max-height: 90vh;

  ${({ theme }) => theme.mediaQueries.xs} {
    width: auto;
    min-width: 360px;
    max-width: 100%;
  }
  ${({ theme }) => theme.mediaQueries.mobile} {
    padding: 24px 16px;
    width: 90vw;
  }
`

const ButtonWrapper = styled.div<HTMLAttributes<HTMLDivElement>>`
  width: max-content;
  cursor: pointer;
`

const Modal: React.FC<Props> = ({
  title,
  onDismiss,
  hideCloseButton = false,
  children,
  style,
  centerTitle,
  titleFontVariant = TextVariant.H5,
  onReturnArrowClick,
  subTitle
}) => {
  const { colors } = useTheme()
  const { isTablet } = useScreenSize()

  return (
    <StyledModal style={style}>
      <Flex alignItems="center" justifyContent="space-between">
        <Flex width="100%" justifyContent={centerTitle ? 'center' : 'initial'} mb="8px" alignItems="center">
          {onReturnArrowClick && (
            <ChevronLeft
              color={colors.white}
              onClick={onReturnArrowClick}
              style={{ cursor: 'pointer', marginRight: '16px' }}
            />
          )}
          <Flex alignItems={isTablet ? 'flex-start' : 'center'} flexDirection={isTablet ? 'column' : 'row'}>
            {title && (
              <Text variant={titleFontVariant} mr="16px">
                {title}
              </Text>
            )}
            {subTitle && (
              <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                {subTitle}
              </Text>
            )}
          </Flex>
        </Flex>
        {!hideCloseButton && (
          <ButtonWrapper onClick={onDismiss}>
            <X color={colors.white} />
          </ButtonWrapper>
        )}
      </Flex>
      {children}
    </StyledModal>
  )
}

export default Modal
